import { isEnhancedNavigationHistoryState } from "@/main/navigation/NavigationHistoryState";

/**
 * Per-history-entry memory of "what this list had loaded", so a Back that remounts the island rebuilds
 * to its prior length instead of refetching page 1.
 *
 * Deliberate non-properties, each load-bearing:
 *
 * - **Not a store.** No `useSyncExternalStore`, no subscribers, no snapshot, no `notifyListeners`. It is
 *   a module-level `Map` read imperatively once at mount and written imperatively on items change. Rule
 *   31 exists because the persistent chrome subscribes to the real stores; a feed write must never be
 *   able to re-render the header. If a later revision ever exposes this through a store, rule 31's
 *   mutator bail-out becomes mandatory and `HeaderInboxStore.contract.spec.ts`'s table must be extended.
 * - **No serialization and no `sessionStorage`.** Items are held BY REFERENCE and the cache dies with the
 *   tab. That is the correct behavior for the release-guard hard reload (`FrontendReleaseGuard` →
 *   `performBrowserNavigation`): a cross-deploy Back should get fresh content, not a resurrected list
 *   built against the previous bundle's DTO shape. It also keeps the module dependency-free, which is
 *   what keeps it inside the rule-27 bundle budget.
 * - **Bounded twice.** LRU over the last {@link MAX_CACHED_NAVIGATION_IDS} navigation ids, and
 *   {@link MAX_CACHED_ITEMS_PER_LIST} items per list.
 *
 * The key is the history entry's `navigationId`, never the URL: `/` → card → Back → card → Back puts two
 * distinct entries on the same URL, and a URL key would restore the wrong list silently.
 */

/** LRU bound on distinct history entries held at once. */
export const MAX_CACHED_NAVIGATION_IDS = 5;

/** Bound on items held for a single list within one history entry. */
export const MAX_CACHED_ITEMS_PER_LIST = 300;

type CachedItem = { paginationParameter?: unknown };

/**
 * navigationId → listKey → items. Insertion order of the outer map IS the LRU order (a write moves its
 * id to the end); `Map` guarantees that ordering, which is why no separate recency structure exists.
 */
const cacheByNavigationId = new Map<string, Map<string, readonly CachedItem[]>>();

/**
 * A cursor lives on the LAST item of each page (`HomeFeedList.toHomeFeedListItems`), and
 * `inferHasMoreFromItems` reads it off the tail. So the resume cursor needs no separate field — but it
 * also means a truncated list must END on a page boundary, or the restored list advertises no next page
 * and pagination stops dead.
 */
function hasUsableCursor(item: CachedItem | undefined): boolean {
    const cursor = item?.paginationParameter;
    if (cursor == null) {
        return false;
    }
    // A BLANK cursor is not a cursor — same rule `inferHasMoreFromItems` applies, restated here so the
    // truncation cannot pick a boundary the consumer will then read as exhausted.
    return typeof cursor !== "string" || cursor.trim() !== "";
}

/**
 * Truncate to the largest prefix within the cap whose tail still carries a usable cursor. Returns
 * `undefined` when no such prefix exists — storing a headless prefix would restore a list that can never
 * paginate again, which is worse than a cache miss.
 */
function truncateToPageBoundary(items: readonly CachedItem[]): readonly CachedItem[] | undefined {
    if (items.length <= MAX_CACHED_ITEMS_PER_LIST) {
        return items;
    }

    for (let index = MAX_CACHED_ITEMS_PER_LIST - 1; index >= 0; index -= 1) {
        if (hasUsableCursor(items[index])) {
            return items.slice(0, index + 1);
        }
    }

    return undefined;
}

/**
 * The `navigationId` of the history entry currently being displayed, or `undefined` when this entry
 * carries no enhanced-navigation state.
 *
 * **`undefined` is a real, reachable case, not a defensive branch.** An entry is unstamped whenever the
 * write that would have stamped it did not happen — a pre-enhanced-navigation entry left over across a
 * release, and (since 2026-07-28) any entry whose write was refused by `refusesUnresolvedUrl`. Callers
 * must treat it as "caching is off for this mount" and fall through to their existing behavior.
 */
export function getCurrentNavigationCacheId(): string | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }

    const state: unknown = window.history.state;
    return isEnhancedNavigationHistoryState(state) ? state.navigationId : undefined;
}

/**
 * Remember `items` for (`navigationId`, `listKey`).
 *
 * An EMPTY list is never stored, and this is a rule-32 fence rather than an optimization: a restored
 * mount seeded with `[]` sets `initialLoadCompleted = true` and `loading = false`, which resolves to
 * `phase === "empty"` and paints the empty state over content the server already rendered.
 */
export function saveNavigationListState<T extends CachedItem>(
    navigationId: string | undefined,
    listKey: string,
    items: readonly T[],
): void {
    if (!navigationId || !listKey || items.length === 0) {
        return;
    }

    const bounded = truncateToPageBoundary(items);
    if (bounded == null || bounded.length === 0) {
        return;
    }

    const existing = cacheByNavigationId.get(navigationId);
    // Re-inserting moves this id to the end of the map's iteration order, which is the LRU touch.
    if (existing) {
        cacheByNavigationId.delete(navigationId);
    }

    const listsForNavigation = existing ?? new Map<string, readonly CachedItem[]>();
    listsForNavigation.set(listKey, bounded.slice());
    cacheByNavigationId.set(navigationId, listsForNavigation);

    while (cacheByNavigationId.size > MAX_CACHED_NAVIGATION_IDS) {
        const oldestNavigationId = cacheByNavigationId.keys().next().value;
        if (oldestNavigationId == null) {
            break;
        }
        cacheByNavigationId.delete(oldestNavigationId);
    }
}

/**
 * The items remembered for (`navigationId`, `listKey`), or `undefined` on a miss.
 *
 * A read neither consumes the entry nor reorders the LRU: restoring the same entry twice (Back, Forward,
 * Back) must behave identically both times, and recency that moved on read would make eviction depend on
 * how a user browsed rather than on what was written.
 */
export function readNavigationListState<T extends CachedItem>(
    navigationId: string | undefined,
    listKey: string,
): T[] | undefined {
    if (!navigationId || !listKey) {
        return undefined;
    }

    const stored = cacheByNavigationId.get(navigationId)?.get(listKey);
    if (stored == null || stored.length === 0) {
        return undefined;
    }

    return stored.slice() as T[];
}

/**
 * Drop everything. The test hook, and the hard-reload path if one is ever wired — note a real hard reload
 * discards the module anyway, which is the point of holding this in memory only.
 */
export function clearNavigationListStateCache(): void {
    cacheByNavigationId.clear();
}
